import React from "react";
import "./App.css";
import { NavBar } from "./NavBar";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";
import { HomePage } from "./pages/HomePage";
import { ApplyPage } from "./pages/ApplyPage";
import { LoginPage } from "./pages/LoginPage";
import { ApplicantDashboardPage } from "./pages/Admin/ApplicantDashboardPage";
import { ApplicationDetailsPage } from "./pages/ApplicationDetailsPage";
import { AdminConfigPage } from "./pages/Admin/AdminConfigPage";
import { UserContext } from "./common/UserContext";
import { Batch, IS_ADMIN_REF, makeRequest, Organization, User } from "./common/api";

import { AssessmentPage } from "./pages/AssessmentPage";
import { BatchesContext } from "./common/BatchesContext";
import { BatchDetailsPage } from "./pages/BatchDetailsPage";

// Initializes the minimal font-awesome icon set
import { Library } from "./common/components/Icons";
import { PasswordResetPage } from "./pages/Utility/PasswordResetPage";
import { ApplyBatchPage } from "./pages/ApplyBatchPage";
import { ApplyOrgPage } from "./pages/ApplyOrgPage";
import { OrgContext, OrgIdContext } from "./common/OrgContext";
import { PaymentsPage } from "./pages/PaymentsPage";
import { CreateOrganizationPage } from "./pages/CreateOrganizationPage";
import {
  captureQueryParams,
  setupAnalytics,
} from "./common/features/Analytics";
import { isUserAdmin } from "./common/auth";
import { EditProcessPage } from "./pages/Admin/EditProcessPage";
Library.noop(); // Hackery because Michael doesn't know how to make modules work properly in TypeScript

export default function App() {
  const [user, setUser] = React.useState<User | null>(null);
  React.useEffect(() => {
    makeRequest("/api/user", "GET").then((u: User) => setUser(u));
  }, []);

  const [batches, setBatches] = React.useState<Batch[] | null>(null);
  React.useEffect(() => {
    makeRequest("/api/batches", "GET").then((data) => {
      setBatches(data);
    });
  }, []);

  const [organization, setOrganization] = React.useState<
    Organization | "NONE" | null
  >(null);

  React.useEffect(() => {
    captureQueryParams();

    makeRequest(`/api/organizationForCurrentDomain`, "GET").then(
      (org) => {
        if (org) {
          if (document.title === "Apply now!") {
            document.title = "Apply to join " + org.name;
          }
          setOrganization(org);
          setupAnalytics(org);
        } else {
          setOrganization("NONE");
        }
      },
      (err) => {
        setOrganization("NONE");
        // No-op
      }
    );
  }, []);

  React.useEffect(() => {
    if (user && organization && organization !== "NONE") {
      IS_ADMIN_REF.isAdmin = isUserAdmin(user, organization._id);
    }
  }, [user, organization])

  return (
    <Router>
      <UserContext.Provider
        value={{ user, setUser, logout: () => setUser(null) }}
      >
        <OrgContext.Provider value={organization}>
          <OrgIdContext.Provider
            value={organization !== "NONE" && organization && organization._id}
          >
            <BatchesContext.Provider value={{ batches, setBatches }}>
              <NavBar></NavBar>
              <Switch>
                <Route path="/login">
                  <LoginPage />
                </Route>
                <Route path="/signup">
                  <LoginPage startOnSignup={true} />
                </Route>
                <Route path="/applications/:applicationId/assessments/:assessmentId">
                  <AssessmentPage />
                </Route>
                <Route path="/application/:applicationId">
                  <ApplicationDetailsPage />
                </Route>
                <Route path="/batch/:batchId">
                  <BatchDetailsPage />
                </Route>
                <Route path="/apply/:orgId/:batchId">
                  <ApplyBatchPage />
                </Route>
                <Route path="/apply/:orgId">
                  <ApplyOrgPage />
                </Route>
                <Route path="/apply">
                  <ApplyPage />
                </Route>
                <Route path="/admin/dashboard">
                  <ApplicantDashboardPage />
                </Route>
                <Route path="/admin/processes/:processId">
                  <EditProcessPage />
                </Route>
                <Route path="/admin/config">
                  <AdminConfigPage />
                </Route>
                <Route path="/payments">
                  <PaymentsPage />
                </Route>
                <Route path="/passwordReset">
                  <PasswordResetPage />
                </Route>
                <Route path="/createOrganization">
                  <CreateOrganizationPage />
                </Route>
                <Route path="/">
                  <HomePage />
                </Route>
              </Switch>
            </BatchesContext.Provider>
          </OrgIdContext.Provider>
        </OrgContext.Provider>
      </UserContext.Provider>
    </Router>
  );
}
